import { useMutation, useQuery } from '@apollo/client';
import { Balance } from 'components/Balance/Balance';
import { Breadcrumbs } from 'components/Breadcrumbs/Breadcrumbs';
import { Button } from 'components/Button/Button';
import Checkbox from 'components/Checkbox/Checkbox';
import { CompanyHeader } from 'components/CompanyHeader/CompanyHeader';
import { CompanyTabs } from 'components/CompanyTabs/CompanyTabs';
import { PageTitle } from 'components/PageTitle/PageTitle';
import { Pagination } from 'components/Pagination/Pagination';
import { SearchFilter } from 'components/SearchFilter/SearchFilter';
import { SortFilter } from 'components/SortFilter/SortFilter';
import { StatusFilter } from 'components/StatusFilter/StatusFilter';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/Table/Table';
import { useToast } from 'components/Toast/use-toast';
import dayjs from 'dayjs';
import { normalizeArray } from 'lib/utils';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { COMPANY_MANUAL_TRANSACTIONS } from 'services/companies/query';
import {
    EXPORT_ENTRIES,
    MANUAL_TRANSACTION_CREATE,
    MANUAL_TRANSACTION_DELETE,
    MANUAL_TRANSACTION_UPDATE,
} from 'services/users/mutation';
import { useCompanyStore } from 'stores/company.store';

import { AddCompanyTransactionModal } from './_components/AddCompanyTransactionModal';
import { CompanyTransactionActions } from './_components/CompanyTransactionActions';
import { ConfirmTransactionsModal } from './_components/ConfirmTransactionsModal';
import { DeleteTransactionModal } from './_components/DeleteTransactionModal';

const PAGE_SIZE = parseInt(localStorage.getItem('pageSize') as any) || 10;
const TYPES = ['CONSUMPTION', 'WINE', 'CATERING', 'DEBIT', 'DEPOSIT', 'CREDIT', 'MEMBERSHIP_FEE'];
const SORT = [
    { value: 'HIGHEST_AMOUNT', label: 'Highest amount' },
    { value: 'LOWEST_AMOUNT', label: 'Lowest amount' },
    { value: 'NEWEST_DATE', label: 'Newest date' },
    { value: 'OLDEST_DATE', label: 'Oldest date' },
];

const POSITIVE_TRANSACTIONS = ['DEPOSIT', 'CREDIT'];

export const CompanyTransactions = () => {
    const { company, getCompany } = useCompanyStore(state => state);
    const [allSelected, setAllSelected] = useState(false);
    const [selectedTransactions, setSelectedTransactions] = useState<any>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [isOpenAdd, setIsOpenAdd] = useState(false);
    const [isOpenDelete, setIsOpenDelete] = useState(false);
    const [exportManualEntries, { loading }] = useMutation(EXPORT_ENTRIES);
    const [addTransactionMutation] = useMutation(MANUAL_TRANSACTION_CREATE);
    const [editingTransaction, setEditingTransaction] = useState<any>(null);
    const { toast } = useToast();
    const [isLoading, setIsLoading] = useState(false);
    const [updateTransactionMutation] = useMutation(MANUAL_TRANSACTION_UPDATE);
    const [manualTransactionDelete] = useMutation(MANUAL_TRANSACTION_DELETE);

    const { t } = useTranslation('documents');
    const { t: b } = useTranslation('translation');
    const [searchParams] = useSearchParams();

    const { data, refetch } = useQuery(COMPANY_MANUAL_TRANSACTIONS, {
        variables: { company: company?.id, first: PAGE_SIZE },
    });

    useEffect(() => {
        const search = searchParams.get('search');
        const sort = searchParams.get('sort');
        const type = searchParams.get('type')?.split(',');
        const page = searchParams.get('page') || '1';

        refetch({
            search: search,
            sort: sort,
            type: type,
            offset: (parseInt(page) - 1) * PAGE_SIZE,
        });
    }, [searchParams, refetch]);

    const selectAll = () => {
        if (allSelected) {
            setSelectedTransactions([]);
            setAllSelected(false);
            return;
        }
        setAllSelected(true);
        setSelectedTransactions(data?.balanceEntries?.edges.map((t: any) => t.node.id));
    };

    const selectTransaction = (id: string) => {
        if (allSelected) setAllSelected(false);
        if (selectedTransactions.includes(id)) {
            setSelectedTransactions(selectedTransactions.filter((transaction: any) => transaction !== id));
            return;
        }
        setSelectedTransactions([...selectedTransactions, id]);
    };

    const exportTransactions = () => {
        if (!selectedTransactions.length) {
            setAllSelected(true);
            setSelectedTransactions(data?.balanceEntries?.edges.map((t: any) => t.node.id));
        }
        setIsOpen(true);
    };

    const confirmExportTransactions = async () => {
        if (!selectedTransactions.length) return;
        const resp = await exportManualEntries({
            variables: {
                balanceEntries: selectedTransactions,
            },
        });
        if (resp?.errors && resp?.errors?.length > 0) {
            failToast(resp.errors[0].message);
        }
        if (resp?.data?.exportManualEntries?.file) {
            window.open(resp?.data?.exportManualEntries?.file, '_blank');
            setIsOpen(false);
            setSelectedTransactions([]);
            setAllSelected(false);
            successToast();
        }
    };

    const handleTransactionAdd = async (data: any) => {
        data.amount = Number(data.amount);
        data.date = dayjs(data.date, 'DD.MM.YYYY').format('YYYY-MM-DD');
        try {
            setIsLoading(true);

            if (editingTransaction) {
                await handleEditTransaction(data);
            } else {
                await handleAddTransaction(data);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleAddTransaction = async (data: any) => {
        data.company = company?.id;
        if (!data.member) delete data.member;
        const resp = await addTransactionMutation({
            variables: { input: data },
        });
        if (resp?.errors && resp?.errors?.length > 0) {
            failToast(resp.errors[0].message);
        }
        if (resp?.data?.manualBalanceEntryCreate?.balanceEntry?.id) {
            toast({
                title: t('addConsumptionSuccess'),
                variant: 'success',
            });
            setIsOpenAdd(false);
            refetch();
            if (company) getCompany(company.id);
        }
    };

    const handleEditTransaction = async (data: any) => {
        const { __typename, ...updateData } = data;
        if (!updateData.member || updateData.member === editingTransaction.member?.id) delete updateData.member;
        const resp = await updateTransactionMutation({
            variables: {
                id: editingTransaction.id,
                input: updateData,
            },
        });
        if (resp?.errors && resp?.errors?.length > 0) {
            failToast(resp.errors[0].message);
        }
        if (resp?.data?.manualBalanceEntryUpdate?.balanceEntry?.id) {
            toast({
                title: t('editConsumptionSuccess'),
                variant: 'success',
            });
            setIsOpenAdd(false);
            setEditingTransaction(null);
            if (company) getCompany(company.id);
        }
    };

    const editTransaction = (transaction: any) => {
        setEditingTransaction(transaction);
        setIsOpenAdd(true);
    };

    const deleteTransaction = async () => {
        setIsLoading(true);
        const resp = await manualTransactionDelete({
            variables: {
                id: editingTransaction.id,
            },
        });
        if (resp?.errors && resp?.errors?.length > 0) {
            failToast(resp.errors[0].message);
        }
        if (resp?.data?.manualBalanceEntryDelete?.success) {
            toast({
                title: t('deleteConsumptionSuccess'),
                variant: 'success',
            });
            setIsOpenDelete(false);
        }
        setEditingTransaction(null);
        setIsLoading(false);
        if (company) getCompany(company.id);
    };

    const handleTransactionModalClose = () => {
        setIsOpenAdd(false);
        setEditingTransaction(null);
    };

    const successToast = () => {
        toast({
            title: t('successExportTitle'),
            variant: 'success',
        });
    };

    const failToast = (message: any) => {
        toast({
            title: t('failToastTitle'),
            description: message || t('failToastSubtitle'),
            variant: 'warning',
        });
    };

    if (!company) return null;

    return (
        <div>
            <Breadcrumbs
                crumbs={[
                    b('breadcrumbs.dashboard'),
                    b('breadcrumbs.members'),
                    b('breadcrumbs.companies'),
                    company.name,
                    b('breadcrumbs.consumptions'),
                ]}
            />
            <CompanyHeader />
            <CompanyTabs />
            <PageTitle>{t('consumptionsTitle')}</PageTitle>

            <div className="flex justify-between">
                <div className="flex gap-4 flex-wrap pb-4">
                    <SearchFilter placeholder={t('searchInvoicesPlaceholder')} />
                    <StatusFilter
                        options={TYPES}
                        label="Type"
                        filterKey="type"
                        translationKeyPrefix="transactionTypes"
                    />
                    <SortFilter options={SORT} />
                </div>
                <div className="flex gap-4">
                    <Button
                        onClick={() => exportTransactions()}
                        variant="outline"
                        disabled={!selectedTransactions.length}
                    >
                        {t('exportButton')}
                    </Button>
                    <Button onClick={() => setIsOpenAdd(true)}>{t('addConsumptionButton')}</Button>
                </div>
            </div>

            <Table className="border">
                <TableHeader>
                    <TableRow>
                        <TableHead className="flex gap-2 items-center">
                            <Checkbox checked={allSelected} onChange={() => selectAll()} />
                            {t('memberLabel')}
                        </TableHead>
                        <TableHead className="whitespace-nowrap">{t('dateLabel')}</TableHead>
                        <TableHead className="whitespace-nowrap">{t('receiptLabel')}</TableHead>
                        <TableHead>{t('typeLabel')}</TableHead>
                        <TableHead>{t('amountLabel')}</TableHead>
                        <TableHead>{t('noteLabel')}</TableHead>
                        <TableHead />
                    </TableRow>
                </TableHeader>
                <TableBody className="hover:cursor-pointer">
                    {data?.balanceEntries?.edges?.length > 0 &&
                        normalizeArray(data.balanceEntries).map((transaction: any) => (
                            <TableRow key={transaction.id} className="text-zinc-700 hover:bg-rose-50">
                                <TableCell onClick={() => selectTransaction(transaction.id)}>
                                    <div className="flex gap-2 items-center w-full">
                                        <Checkbox
                                            onChange={() => selectTransaction(transaction.id)}
                                            checked={selectedTransactions.includes(transaction.id)}
                                        />
                                        {transaction.member
                                            ? `${transaction.member.lastName}, ${transaction.member.firstName}`
                                            : '-'}
                                    </div>
                                </TableCell>
                                <TableCell onClick={() => selectTransaction(transaction.id)}>
                                    {dayjs(transaction.date).format('DD.MM.YYYY')}
                                </TableCell>
                                <TableCell onClick={() => selectTransaction(transaction.id)}>
                                    {transaction.invoiceNumber}
                                </TableCell>
                                <TableCell onClick={() => selectTransaction(transaction.id)}>
                                    {t(transaction.type)}
                                </TableCell>
                                <TableCell onClick={() => selectTransaction(transaction.id)}>
                                    <div className="flex items-center justify-between gap-2 max-w-[150px] min-w-[max-content]">
                                        <Balance
                                            classname={
                                                POSITIVE_TRANSACTIONS.includes(transaction.type)
                                                    ? 'text-green-700'
                                                    : 'text-red-700'
                                            }
                                            balance={{ amount: transaction.amount, currency: 'CHF' }}
                                        />
                                        <div
                                            className={`w-6 h-6 rounded-full flex items-center justify-center text-md font-bold
                                                ${
                                                    POSITIVE_TRANSACTIONS.includes(transaction.type)
                                                        ? 'bg-green-100 text-green-700'
                                                        : 'bg-red-100 text-red-700'
                                                }`}
                                        >
                                            <div>{POSITIVE_TRANSACTIONS.includes(transaction.type) ? '+' : '–'}</div>
                                        </div>
                                    </div>
                                </TableCell>
                                <TableCell onClick={() => selectTransaction(transaction.id)}>
                                    {transaction.note}
                                </TableCell>
                                <TableCell className="py-3 justify-end flex">
                                    <CompanyTransactionActions
                                        company={company}
                                        transaction={transaction}
                                        onEdit={() => editTransaction(transaction)}
                                        onDelete={() => {
                                            setEditingTransaction(transaction);
                                            setIsOpenDelete(true);
                                        }}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                </TableBody>
            </Table>
            {data?.balanceEntries?.totalCount === 0 && (
                <div className={'py-5 text-center text-zinc-700'}>{t('noTransactions')}</div>
            )}
            <Pagination pageInfo={data?.balanceEntries?.pageInfo} total={data?.balanceEntries?.totalCount} />
            <ConfirmTransactionsModal
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                onConfirm={() => confirmExportTransactions()}
                isLoading={loading}
            />
            <DeleteTransactionModal
                isOpen={isOpenDelete}
                onClose={() => setIsOpenDelete(false)}
                onConfirm={deleteTransaction}
                isLoading={isLoading}
            />
            <AddCompanyTransactionModal
                isOpen={isOpenAdd}
                onClose={() => handleTransactionModalClose()}
                onAdd={handleTransactionAdd}
                editTransaction={editingTransaction}
                isLoading={isLoading}
                company={company}
            />
        </div>
    );
};
